// src/pages/modules/StockPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchStocks, clearError } from 'store/slices/stockSlice';
import { Loader2 } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import { formatDateTime } from 'utlis/formateDateTime';

const StockPage = () => {
  usePermissionSocket();

  const dispatch = useDispatch();
  const { stocks, pagination, isLoading, error } = useSelector((s) => s.stock);

  const { canRead, canExport } = usePermissions('stock');

  // Pagination & Search State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Reset to page 1 when searching
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Refetch with search
  const refetch = useCallback(() => {
    if (canRead) {
      dispatch(fetchStocks({ page, pageSize, search: searchTerm }));
    }
  }, [dispatch, page, pageSize, searchTerm, canRead]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load stock levels');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleExport = () => {
    if (!canExport) {
      toast.error("You don't have permission to export");
      return;
    }
    toast.info("Export feature coming soon");
  };

  const columns = [
      {
      header: "Items",
      cell: ({ row }) => (
        <div>
          <p>{row.original.sub_category_name ?? '-'}</p>
          <p className="text-xs text-muted-foreground">
            {row.original.category_name ?? '-'}
          </p>
        </div>
      ),
    },
    // {
    //   header: 'Sub-category',
    //   cell: ({ row }) => (
    //     <span className="font-medium">{row.original.sub_category_name || '—'}</span>
    //   ),
    // },
    // {
    //   header: 'Category',
    //   cell: ({ row }) => (
    //     <span className="text-sm text-muted-foreground">
    //       {row.original.category_name || '—'}
    //     </span>
    //   ),
    // },
    {
      accessorKey: 'quantity',
      header: 'Current Stock',
      cell: ({ row }) => {
        const qty = row.original.quantity;
        return (
          <Badge
            variant={qty === 0 ? 'destructive' : qty < 10 ? 'secondary' : 'default'}
            className="font-mono text-sm"
          >
            {qty}
          </Badge>
        );
      },
    },
       {
      accessorKey: 'quantity',
      header: 'Buffer Stock',
      cell: ({ row }) => {
        const qty = row.original.buffer_stock;
        return (
          <Badge
            variant={qty === 0 ? 'destructive' : qty < 10 ? 'secondary' : 'default'}
            className="font-mono text-sm"
          >
            {qty}
          </Badge>
        );
      },
    },
    {
  header: "Stock Status",
  cell: ({ row }) => {
    const current = row.original.quantity;
    const buffer = row.original.buffer_stock;

    let status = "";
    let variant = "";
    let color = "";

    if (current > buffer) {
      status = "In Stock";
      variant = "success";     // green
    } else {
      status = "Reorder";
      variant = "destructive"; // red
    }

    return (
      <Badge variant={variant} className="font-medium">
        {status}
      </Badge>
    );
  },
},
    {
      accessorKey: 'created_at',
      header: 'Last Updated',
      cell: ({ row }) => 
        (formatDateTime(row.original.updated_at || row.original.created_at) ?? '-')
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Current Stock Levels"
        description="Real-time stock quantities across all sub-categories"
      />
{/* 
      {isLoading && stocks.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin mr-3" />
          <span className="text-lg">Loading stock levels...</span>
        </div>
      )} */}
{/* 
      {!canRead && !isLoading && (
        <div className="text-center py-16 text-muted-foreground text-lg">
          You don't have permission to view stock levels.
        </div>
      )} */}

      {canRead && (
        <DataTable
          data={stocks}
          totalCount={pagination.count}
          columns={columns}
          searchPlaceholder="Search by sub-category, category name..."
          // showExport={canExport}
          showExport={false}
          onExport={canExport ? handleExport : undefined}
          showImport={false}
          // Server-side pagination
          pageIndex={page - 1}
          pageSize={pageSize}
          pageCount={pagination.count ? Math.ceil(pagination.count / pagination.page_size) : 1}
          onPageChange={(i) => setPage(i + 1)}
          onPageSizeChange={setPageSize}
          onSearch={setSearchTerm}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default StockPage;